import { get } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { createForm } from '../../models/Form';
import { createFormField } from '../../models/FormField';
import OrderCancellationService from '../../services/OrderCancellationService';
import RequestState from '../../types/RequestState';
import { createErrorModel } from '../../util/error';
import {
  isEmail,
  isInteger,
  minLength,
  notBlank,
  privacyStatementAccepted,
} from '../../util/formValidators';
import messages from './messages';
import { createOrderCancellationPayload } from './utils';

const Step = {
  DETAILS: 'DETAILS',
  CONFIRM: 'CONFIRM',
  SUCCESS: 'SUCCESS',
};

const useOrderCancellationForm = ({
  apiWrapper,
  configStore,
  languageStore,
  order = null,
  orderId = null,
  uiStore,
  intl,
}) => {
  const [currentStep, setCurrentStep] = useState(Step.DETAILS);
  const [cancellationScope, setCancellationScopeState] = useState('whole');
  const [requestState, setRequestState] = useState(RequestState.NONE);
  const [submitError, setSubmitError] = useState(null);
  const [submitRequestId, setSubmitRequestId] = useState(0);
  const formRef = useRef(null);
  const prefilledOrderIdRef = useRef(null);

  if (!formRef.current) {
    const emailLabel = intl.formatMessage(messages.emailLabel);
    const orderIdentifierLabel = intl.formatMessage(
      messages.orderIdentifierLabel
    );
    const productDescriptionLabel = intl.formatMessage(
      messages.productDescriptionLabel
    );
    const privacyStatementLabel = intl.formatMessage(
      messages.privacyStatementLabel
    );

    formRef.current = createForm({
      email: createFormField({}, [
        minLength(emailLabel, configStore.formValidation.email.minLength),
        isEmail(emailLabel),
      ]),
      orderIdentifier: createFormField({}, [
        notBlank(orderIdentifierLabel),
        isInteger(orderIdentifierLabel),
      ]),
      cancellationScope: createFormField({ value: 'whole' }, [
        notBlank(intl.formatMessage(messages.cancellationScopeLabel)),
      ]),
      productDescription: createFormField(
        {},
        [notBlank(productDescriptionLabel)],
        () =>
          formRef.current.fields.get('cancellationScope').value === 'partial'
      ),
      additionalMessage: createFormField({}),
      privacyStatement: createFormField({}, [
        privacyStatementAccepted(privacyStatementLabel),
      ]),
    });
  }

  const form = formRef.current;
  const cancelWholeOrder = cancellationScope !== 'partial';
  const hasOrderContext = !!orderId;
  const productDescription = cancelWholeOrder
    ? ''
    : (form.fields.get('productDescription').value || '').trim();

  const summary = {
    email: form.fields.get('email').value || '',
    orderIdentifier: form.fields.get('orderIdentifier').value || '',
    scope: cancelWholeOrder
      ? intl.formatMessage(messages.wholeOrderSummaryValue)
      : intl.formatMessage(messages.partialOrderSummaryValue),
    products: cancelWholeOrder ? [] : [productDescription].filter(Boolean),
    additionalMessage: (
      form.fields.get('additionalMessage').value || ''
    ).trim(),
  };

  const apiErrorMessages = (() => {
    if (!submitError) {
      return null;
    }

    const messagesFromResponse =
      get(submitError, 'response.data.messages') ||
      Object.values(get(submitError, 'response.data.errors', {}));

    return messagesFromResponse && messagesFromResponse.length > 0
      ? messagesFromResponse
      : null;
  })();

  useEffect(() => {
    if (!order) {
      return;
    }

    const currentOrderId = String(order.id);

    if (prefilledOrderIdRef.current === currentOrderId) {
      return;
    }

    const customerInfo =
      order.getCustomerInfo?.() || order.getCustomerShippingInfo?.();

    if (customerInfo) {
      const email = (customerInfo.email || '').trim();

      if (email) {
        form.fields.get('email').setValue(email);
      }
    }

    form.fields.get('orderIdentifier').setValue(currentOrderId);
    prefilledOrderIdRef.current = currentOrderId;
  }, [form.fields, order]);

  const setCancellationScope = (value) => {
    setCancellationScopeState(value);
    form.fields.get('cancellationScope').setValue(value);
    form.fields.get('cancellationScope').setShouldValidate(true);

    if (value === 'whole') {
      form.fields.get('productDescription').setValue(null);
    }
  };

  const clearSubmitState = () => {
    setSubmitError(null);
    setRequestState(RequestState.NONE);
  };

  const goToConfirmStep = () => {
    form.validate();

    if (form.valid) {
      setCurrentStep(Step.CONFIRM);
    }
  };

  const goToDetailsStep = () => {
    clearSubmitState();
    setCurrentStep(Step.DETAILS);
  };

  const submit = async () => {
    form.validate();

    if (!form.valid) {
      setCurrentStep(Step.DETAILS);
      return;
    }

    setRequestState(RequestState.LOADING);
    setSubmitError(null);

    await uiStore.recaptchaValidation.getCaptchaToken();

    const values = form.getFieldValues();

    if (uiStore.recaptchaValidation.isValid) {
      values.recaptcha = uiStore.recaptchaValidation.token;

      const payload = createOrderCancellationPayload({
        values,
        activeLocale: languageStore.activeLocale,
        cancelWholeOrder,
        productDescription,
      });

      try {
        await OrderCancellationService.submit(apiWrapper, payload);
        setRequestState(RequestState.LOADED);
        setCurrentStep(Step.SUCCESS);
      } catch (error) {
        setSubmitError(error);
        setSubmitRequestId((prevState) => prevState + 1);
        setRequestState(RequestState.ERROR);
      }
    } else {
      setRequestState(RequestState.ERROR);
    }

    if (configStore.recaptcha.enabled) {
      uiStore.recaptchaValidation.resetCaptchaToken();
    }
  };

  return {
    apiErrorMessages,
    cancelWholeOrder,
    currentStep,
    form,
    goToConfirmStep,
    goToDetailsStep,
    isOrderContextPrefilled: hasOrderContext && !!order,
    orderContextLoadFailed: hasOrderContext && !order,
    requestState,
    setCancellationScope,
    submit,
    submitErrorModel:
      submitError && get(submitError, 'response.status') !== 422
        ? createErrorModel(submitError)
        : null,
    submitRequestId,
    summary,
  };
};

export default useOrderCancellationForm;
